import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ContactHeroSection = ({ onContactFormFocus }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  const translations = {
    en: {
      headline: "Connect with ERP Experts",
      subheadline: "Get personalized consultation from aluminum extrusion specialists across India. We're here to help transform your manufacturing business.",
      primaryCTA: "Start Consultation",
      secondaryCTA: "Call Now",
      phoneNumber: "+91 98765 43210",
      trustMetrics: {
        responseTime: "< 2 Hours Response Time",
        consultations: "1000+ Consultations Completed",
        satisfaction: "98% Customer Satisfaction"
      }
    },
    hi: {
      headline: "ERP विशेषज्ञों से जुड़ें",
      subheadline: "भारत भर के एल्यूमीनियम एक्सट्रूज़न विशेषज्ञों से व्यक्तिगत परामर्श प्राप्त करें। हम आपके निर्माण व्यवसाय को बदलने में मदद करने के लिए यहां हैं।",
      primaryCTA: "परामर्श शुरू करें",
      secondaryCTA: "अभी कॉल करें",
      phoneNumber: "+91 98765 43210",
      trustMetrics: {
        responseTime: "< 2 घंटे प्रतिक्रिया समय",
        consultations: "1000+ परामर्श पूर्ण",
        satisfaction: "98% ग्राहक संतुष्टि"
      }
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const handleCallNow = () => {
    window.location.href = 'tel:+919876543210';
  };

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-primary-50 via-background to-surface-100 pt-20">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%23000000%22 fill-opacity=%220.1%22%3E%3Ccircle cx=%227%22 cy=%227%22 r=%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content Column */}
          <div className="space-y-8">
            {/* Main Headline */}
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight">
                {t.headline}
              </h1>
              
              <p className="text-lg md:text-xl text-text-secondary leading-relaxed max-w-2xl">
                {t.subheadline}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="primary"
                size="lg"
                onClick={onContactFormFocus}
                className="bg-gradient-accent hover:bg-accent-600 shadow-cta hover-scale"
                iconName="MessageCircle"
                iconPosition="left"
                iconSize={20}
              >
                {t.primaryCTA}
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                onClick={handleCallNow}
                className="border-primary text-primary hover:bg-primary-50"
                iconName="Phone"
                iconPosition="left"
                iconSize={20}
              >
                {t.secondaryCTA}
              </Button>
            </div>

            {/* Trust Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8">
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                  <Icon name="Clock" size={20} color="var(--color-success)" />
                  <span className="text-2xl font-bold text-primary">2h</span>
                </div>
                <p className="text-sm text-text-secondary">{t.trustMetrics.responseTime}</p>
              </div>
              
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                  <Icon name="Users" size={20} color="var(--color-success)" />
                  <span className="text-2xl font-bold text-primary">1000+</span>
                </div>
                <p className="text-sm text-text-secondary">{t.trustMetrics.consultations}</p>
              </div>
              
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start space-x-2 mb-2">
                  <Icon name="Star" size={20} color="var(--color-success)" />
                  <span className="text-2xl font-bold text-primary">98%</span>
                </div>
                <p className="text-sm text-text-secondary">{t.trustMetrics.satisfaction}</p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white rounded-xl p-6 shadow-lg border border-border">
              <h3 className="font-semibold text-primary mb-4">
                {currentLanguage === 'en' ? 'Quick Contact' : 'त्वरित संपर्क'}
              </h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Icon name="Phone" size={18} color="var(--color-primary)" />
                  <span className="text-text-secondary">{t.phoneNumber}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="Mail" size={18} color="var(--color-primary)" />
                  <span className="text-text-secondary">contact@alumerp.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="MapPin" size={18} color="var(--color-primary)" />
                  <span className="text-text-secondary">
                    {currentLanguage === 'en' ?'Mumbai, Delhi, Bangalore, Chennai' :'मुंबई, दिल्ली, बैंगलोर, चेन्नई'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Visual Column */}
          <div className="relative">
            <div className="relative bg-white rounded-2xl shadow-lg p-8 border border-border">
              {/* Mock Contact Dashboard */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-primary">Support Dashboard</h3>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-success rounded-full"></div>
                    <span className="text-sm text-text-secondary">Online</span>
                  </div>
                </div>
                
                {/* Support Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-surface-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-text-secondary">Avg Response</span>
                      <Icon name="Clock" size={16} color="var(--color-success)" />
                    </div>
                    <div className="text-2xl font-bold text-primary">1.8h</div>
                    <div className="text-xs text-success">Within SLA</div>
                  </div>
                  
                  <div className="bg-surface-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-text-secondary">Experts Online</span>
                      <Icon name="Users" size={16} color="var(--color-success)" />
                    </div>
                    <div className="text-2xl font-bold text-primary">12</div>
                    <div className="text-xs text-success">Ready to help</div>
                  </div>
                </div>
                
                {/* Support Channels */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-surface-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name="Phone" size={16} color="var(--color-primary)" />
                      <span className="text-sm text-text-secondary">Phone Support</span>
                    </div>
                    <span className="text-xs text-success font-medium">Available</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-surface-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name="MessageCircle" size={16} color="var(--color-primary)" />
                      <span className="text-sm text-text-secondary">Live Chat</span>
                    </div>
                    <span className="text-xs text-success font-medium">Online</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-surface-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name="Video" size={16} color="var(--color-primary)" />
                      <span className="text-sm text-text-secondary">Video Call</span>
                    </div>
                    <span className="text-xs text-success font-medium">Available</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -top-4 -right-4 bg-success text-white rounded-full p-3 shadow-lg">
              <Icon name="MessageCircle" size={24} />
            </div>
            
            <div className="absolute -bottom-4 -left-4 bg-accent text-white rounded-full p-3 shadow-lg">
              <Icon name="Phone" size={24} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactHeroSection;